<template>
  <v-app>
    <Toolbar v-if="currentUser && !shouldImportUsers && $route.name !== 'login'" />
    <NavigationDrawer v-if="currentUser && !shouldImportUsers && $route.name !== 'login'" />

    <ConfirmDialog />
    <Snackbar />
    <TwoFactorReminderDialog />

    <v-main class="transition-none">
      <router-view />
    </v-main>

    <UpdateNotifier v-if="currentUser" />
    <OnScreenKeyboard v-if="isPrintTerminal" />
  </v-app>
</template>

<script>
import Toolbar from './components/Toolbar';
import NavigationDrawer from './components/NavigationDrawer';
import Snackbar from './components/Snackbar';
import ConfirmDialog from './components/ConfirmDialog';
import { mapState } from 'vuex';
import printLogService from '@/api/print-log-service';
import OnScreenKeyboard from '@/components/OnScreenKeyboard';
import { getLocalStorageItem, removeLocalStorageItem } from '@/util/local-storage';
import UpdateNotifier from '@/components/UpdateNotifier';
import TwoFactorReminderDialog from './components/TwoFactorReminderDialog.vue'

export default {
  name: 'App',

  components: {
    UpdateNotifier,
    OnScreenKeyboard,
    ConfirmDialog,
    Snackbar,
    Toolbar,
    NavigationDrawer,
    TwoFactorReminderDialog,
  },

  watch: {
    isPrintTerminal: {
      handler(newValue) {
        if (newValue) {
          this.addPrintListener();
        } else {
          this.removePrintListener();
        }
      },
      immediate: true,
    },
  },

  computed: {
    ...mapState('settings', ['isPrintTerminal']),

    currentUser() {
      return this.$store.getters['auth/currentUser'];
    },

    shouldImportUsers() {
      return this.$store.state.auth.shouldImportUsers;
    },
  },

  created() {
    // cleaning up just in case page was refreshed during a token refresh request
    removeLocalStorageItem('refreshingToken');

    this.$store.commit('settings/LOAD_SETTINGS');
    this.addTouchListener();
    this.addLocalStorageListener();
    if (this.$vuetify.breakpoint.lgAndUp) {
      this.$store.commit('settings/UPDATE_SETTING', {
        key: 'sidenavOpen',
        value: true,
      });
    }
    this.getDeviceAsPrintTerminalStatus();
  },

  methods: {
    addLocalStorageListener() {
      // handles access token getting refreshed in a different tab
      window.addEventListener('storage', (event) => {
        if (event.key !== 'accessToken') {
          return;
        }
        this.$store.commit('auth/SET_ACCESS_TOKEN', event.newValue);
      });
    },

    addTouchListener() {
      window.addEventListener('touchstart', () => {
        if (!this.isTouchDevice) {
          this.$store.commit('settings/SET_TOUCH_DEVICE');
        }
      });
    },

    addPrintListener() {
      window.addEventListener('afterprint', this.logPrint);
    },

    removePrintListener() {
      window.removeEventListener('afterprint', this.logPrint);
    },

    logPrint() {
      if (!this.isPrintTerminal) {
        return;
      }
      printLogService.logPrint({ url: window.location.href });
    },

    getDeviceAsPrintTerminalStatus() {
      const terminalStatus = getLocalStorageItem('this_device_is_print_terminal');
      if (terminalStatus) {
        this.$store.commit('settings/SET_PRINT_TERMINAL', true);
      }
    },
  },
};
</script>

<style lang="scss">
@import '@fontsource/roboto/100.css';
@import '@fontsource/roboto/300.css';
@import '@fontsource/roboto/400.css';
@import '@fontsource/roboto/500.css';
@import '@fontsource/roboto/700.css';
@import '@fontsource/roboto/900.css';
@import './styles.scss';
@import './vuetify-customizations.scss';
</style>
